import { gql } from '@apollo/client';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useMemo } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';
import { VariantPackGroup } from '@/contexts/item-context-types';
import { VariantPackGroupType } from '@/enums';
import {
  VariantPackInfoContext_GetGroupsQuery,
  VariantPackInfoContext_GetGroupsQueryVariables,
} from '@/types';

export const GET_VARIANT_PACK_GROUPS_QUERY = gql`
  query VariantPackInfoContext_GetGroups(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getVariantPackGroups(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      translated_display_name
      variant_pack_group {
        badge_variant_pack_group_rels {
          badge {
            ...VariantPackInfoContext_GroupBadgeFields
          }
          uuid
        }
        type
        uuid
        variant_pack_group_members(
          where: { member_variant_pack_group_uuid: { _is_null: false } }
        ) {
          is_direct_member_group
          member_variant_pack_group_uuid
          uuid
        }
      }
      variant_pack_group_id
    }
  }

  fragment VariantPackInfoContext_GroupBadgeFields on badges {
    background_color
    name
    uuid
    image_url
    short_name
    sort_order
  }
`;

export interface GroupMap {
  [groupUUID: string]: string[];
}

export interface VariantPackGroupMap {
  [groupUUID: string]: VariantPackGroup | undefined;
}

export const useVariantPackGroup = () => {
  const {
    isGuestMode,
    selectedRestaurantRelationship,
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const supplierUUID = selectedRestaurantRelationship.supplierUUID;
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const variantPackGroupsResult = useWrappedQuery<
    VariantPackInfoContext_GetGroupsQuery,
    VariantPackInfoContext_GetGroupsQueryVariables
  >(GET_VARIANT_PACK_GROUPS_QUERY, {
    context: {
      anonymous: isGuestMode,
    },
    fetchPolicy: 'no-cache',
    skip: !itemGroupsEnabled || !supplierUUID || !restaurantUUID,
    variables: {
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  const childVariantPackGroupMap = useMemo(() => {
    return (
      variantPackGroupsResult.data?.getVariantPackGroups || []
    ).reduce<GroupMap>((acc, vpg) => {
      acc[vpg.variant_pack_group_id] = [];
      const vpGroupMembers = vpg.variant_pack_group?.variant_pack_group_members;

      if (vpGroupMembers) {
        for (const member of vpGroupMembers) {
          const memberUUID = member.member_variant_pack_group_uuid;
          if (memberUUID) {
            acc[vpg.variant_pack_group_id].push(memberUUID);
          }
        }
      }

      return acc;
    }, {});
  }, [variantPackGroupsResult]);

  const parentVariantPackGroupMap = useMemo(() => {
    return (
      variantPackGroupsResult.data?.getVariantPackGroups || []
    ).reduce<GroupMap>((acc, vpg) => {
      const vpGroupMembers = vpg.variant_pack_group?.variant_pack_group_members;

      if (vpGroupMembers) {
        for (const member of vpGroupMembers) {
          const memberUUID = member.member_variant_pack_group_uuid;
          if (memberUUID) {
            if (!acc[memberUUID]) {
              acc[memberUUID] = [vpg.variant_pack_group_id];
            } else {
              acc[memberUUID].push(vpg.variant_pack_group_id);
            }
          }
        }
      }

      return acc;
    }, {});
  }, [variantPackGroupsResult]);

  const variantPackGroupMap = useMemo(() => {
    return (
      variantPackGroupsResult.data?.getVariantPackGroups || []
    ).reduce<VariantPackGroupMap>((acc, vpg) => {
      const directUUIDs: string[] = [];
      const indirectUUIDs: string[] = [];
      const vpGroupMembers = vpg.variant_pack_group?.variant_pack_group_members;

      if (vpGroupMembers) {
        for (const subgroup of vpGroupMembers) {
          const subgroupUUID = subgroup.member_variant_pack_group_uuid;
          if (subgroupUUID && subgroup.is_direct_member_group) {
            directUUIDs.push(subgroupUUID);
          } else if (subgroupUUID) {
            indirectUUIDs.push(subgroupUUID);
          }
        }
      }

      acc[vpg.variant_pack_group_id] = {
        badges:
          vpg.variant_pack_group?.badge_variant_pack_group_rels?.map(
            (rel) => rel.badge,
          ) || [],
        directSubgroupUUIDs: directUUIDs,
        indirectSubgroupUUIDs: indirectUUIDs,
        name: vpg.translated_display_name,
        type: vpg.variant_pack_group?.type as VariantPackGroupType,
        uuid: vpg.variant_pack_group_id,
      };

      return acc;
    }, {});
  }, [variantPackGroupsResult]);

  return {
    childVariantPackGroupMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  };
};
